import { useCallback, useEffect, useState } from 'react';

// sessionStorage ist pro Browser-Tab isoliert und wird beim Schliessen des Tabs
// geloescht. Nichts davon verlaesst den Browser - andere Nutzer koennen den
// Chatverlauf also weder sehen noch wiederherstellen.

const SESSION_ID_KEY = 'lfo.session.id';

function randomId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** Stabile, zufaellige ID fuer den aktuellen Tab. */
export function useSessionId() {
    const [id] = useState(() => {
        try {
            const existing = sessionStorage.getItem(SESSION_ID_KEY);
            if (existing) return existing;
            const created = randomId();
            sessionStorage.setItem(SESSION_ID_KEY, created);
            return created;
        } catch {
            return randomId(); // z. B. bei blockiertem Storage
        }
    });
    return id;
}

/** Direkter Lesezugriff auf sessionStorage mit Fallback. */
export function readSession(key, fallback) {
    try {
        const stored = sessionStorage.getItem(key);
        return stored === null ? fallback : JSON.parse(stored);
    } catch {
        return fallback;
    }
}

/** Direkter Schreibzugriff auf sessionStorage, Fehler werden verschluckt. */
export function writeSession(key, value) {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
        /* Storage voll oder deaktiviert */
    }
}

/** useState, das seinen Wert nur in sessionStorage spiegelt. */
export default function useSessionState(key, initialValue) {
    const [value, setValue] = useState(() => {
        try {
            const stored = sessionStorage.getItem(key);
            return stored === null ? initialValue : JSON.parse(stored);
        } catch {
            return initialValue;
        }
    });

    useEffect(() => {
        try {
            sessionStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Storage voll oder deaktiviert - der State im Speicher bleibt gueltig.
        }
    }, [key, value]);

    const clear = useCallback(() => {
        try {
            sessionStorage.removeItem(key);
        } catch {
            /* ignorieren */
        }
        setValue(initialValue);
    }, [key, initialValue]);

    return [value, setValue, clear];
}
